import { useLayoutEffect } from "react";
import { useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import SmallButtonWrapper from "@/components/atoms/button-wrapper/small-button-wrapper";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import { Quote, RenewalPage } from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, BRAND_AVANTI } from "@/constants";
import { usePromoBanner } from "@/hooks/use-promo-banner";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { RootState } from "@/store/root-reducer";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";
import headerClassNames from "@/utils/get-brand-quote-cover-top-classes.utils";
import isShowExclusiveBanner from "@/utils/online-exclusive-price-banner-show.utils";

import AppliedPromo from "../../quote-section/quote-cover/applied-promo";
import styles from "../../quote-section/quote-cover/cover-level-selection-card-top/cover-level-selection-card-top.module.css";
import OnlineExclusiveBanner from "../../quote-section/quote-cover/online-exclusive-price-banner/online-exclusive-banner";

type HeaderClassNamesType = {
  headerClassName: string;
  headerDetaisClassNames: {
    mainTextClassName: string;
    subTextClassName: string;
  };
  topSectionclassName: string;
};

type CoverPackageProps = {
  keyValue: string;
  type?: string;
  cardTitle?: string;
  application?: string;
  onlineExclusiveBannerLabel?: string;
  id?: number;
  cardTopData?: {
    selectBtnInactiveLable?: string;
    selectBtnActiveLable?: string;
    isLoading?: boolean;
  };
  slectState?: boolean;
  onSelect?: (val: string, state: boolean, index: number) => void;
  blueBannerRef?: React.RefObject<HTMLDivElement | null>;
  setBlueBannerHeight?: (height: number) => void;
  brand?: "stasure" | "avanti";
};

function Cover({
  keyValue,
  cardTitle,
  cardTopData = {},
  type = "",
  application,
  // onlineExclusiveBannerLabel,
  id = 0,
  slectState = false,
  onSelect,
  brand,
  blueBannerRef,
  setBlueBannerHeight,
}: Readonly<CoverPackageProps>) {
  const headerClassNamesList: HeaderClassNamesType =
    headerClassNames[keyValue as keyof typeof headerClassNames];
  const { topSectionclassName } = headerClassNamesList;

  const isQuoteDisabled = useQuoteDisabled();
  const { isPromoBannerActive } = usePromoBanner();

  const { current } = useWizard();

  const { attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );

  const appliedPromoCode = useSelector(
    (state: RootState) =>
      state.quote.attributes?.paymentDetails?.discounts?.promoCode
  );

  // Helper: Check if valid promo code is applied
  const isPromoCodeApplied =
    isPromoBannerActive && appliedPromoCode?.valid && appliedPromoCode?.code;

  // Hide strikethrough if promo banner is active but promo code is NOT applied
  const isHideStrikethroughAndExclusivePrice =
    current.uiElementControls?.isHideStrikethroughAndExclusivePrice ||
    (isPromoBannerActive && !isPromoCodeApplied);

  const cover: PolicyPackagePrice | undefined =
    attributes?.priceResult?.policyPackagePrice.find(
      (coverPackage: PolicyPackagePrice) =>
        coverPackage?.policyType === type &&
        coverPackage?.policyPackage === keyValue
    );

  // Helper: Check if promo discount exists
  const hasPromoDiscount =
    isPromoCodeApplied &&
    cover?.onlinePresentationTotalWithDiscount &&
    cover?.onlinePresentationTotalWithDiscount < (cover?.total ?? 0);

  const showPrice = (value: number | undefined) => {
    return value
      ? `${
          (current.brandSpecificParams?.currencySymbol as string) ?? ""
        }${value.toFixed(2)}`
      : "";
  };

  const renderTotalPrice = (
    total: number,
    onlinePresentationTotalWithDiscount?: number
  ) => {
    if (cover) {
      return showPrice(onlinePresentationTotalWithDiscount ?? total);
    }

    return "";
  };
  const showOfflinePrice = (value: number) => {
    // Hide strikethrough price if promo banner is active but code not applied
    if (isHideStrikethroughAndExclusivePrice) {
      return null;
    }
    return (
      <span
        className={`line-through text-3xs leading-[21px] font-normal font-body-font ${
          brand === BRAND_AVANTI
            ? "text-surface-neutral-option-9"
            : "text-dark-text-on-primary"
        } `}
      >
        {value
          ? `${
              (current.brandSpecificParams?.currencySymbol as string) ?? ""
            }${value.toFixed(2)}`
          : ""}
      </span>
    );
  };

  function handleOfflinePrice(
    showOfflinePriceFunction: (price: number) => React.JSX.Element | null,
    finalRetailENBPrice?: number,
    offlineTotal?: number,
    showOfflineTotalPrice?: boolean,
    loyaltyDiscountEligible?: boolean,
    app?: string
  ): string | React.JSX.Element | null {
    if (showOfflineTotalPrice && app === Quote) {
      return showOfflinePriceFunction(offlineTotal ?? 0);
    }

    if (loyaltyDiscountEligible && app === RenewalPage) {
      return showOfflinePriceFunction(finalRetailENBPrice ?? 0);
    }

    // For AMT with monthly payment when promo code is applied, show strikethrough
    if (
      type === ANNUAL_MULTI_TRIP &&
      cover?.collectionAmount &&
      isPromoCodeApplied &&
      cover?.total &&
      app === Quote
    ) {
      return showOfflinePriceFunction(cover.total);
    }

    return "";
  }

  const renderTotalPriceWithoutDiscounts = (
    offlineTotal?: number,
    app?: string,
    loyaltyDiscountEligible?: boolean,
    finalRetailENBPrice?: number,
    isShowOfflinePrice?: boolean
  ) => {
    if (isShowOfflinePrice && app === Quote) {
      return showOfflinePrice(offlineTotal ?? 0);
    }
    if (loyaltyDiscountEligible && app === RenewalPage) {
      return showOfflinePrice(finalRetailENBPrice ?? 0);
    }
    return null;
  };

  const offlinePrice = handleOfflinePrice(
    showOfflinePrice,
    cover?.finalRetailENBPrice,
    cover?.offlineTotal,
    cover?.showOfflinePrice,
    cover?.loyaltyDiscountEligible,
    application
  );

  const renderExclusiveBanner = (
    isShow: boolean,
    bgColor: string,
    bannerLabel?: string,
    app?: string,
    showOfflineTotalPrice?: boolean,
    loyaltyDiscountEligible?: boolean
  ) => {
    if (
      (app === Quote && showOfflineTotalPrice && isShow) ||
      (app === RenewalPage && isShow && loyaltyDiscountEligible)
    ) {
      return (
        <div
          ref={blueBannerRef}
          className={`justify-items-start ${
            brand === BRAND_AVANTI ? "pl-spacing-m" : "pl-spacing-s"
          } ${offlinePrice !== "" && "pt-spacing-s"} pr-spacing-xs`}
        >
          <div className="z-10 flex justify-center ">
            <OnlineExclusiveBanner
              onlineExclusiveBannerLabel={bannerLabel ?? ""}
              background={bgColor}
              dataTestid={`${cover?.policyType}-${cover?.policyPackage}-mobile`}
              noBorder
              fontWeight={400}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  useLayoutEffect(() => {
    if (setBlueBannerHeight) {
      const height =
        blueBannerRef?.current?.getBoundingClientRect().height ?? 0;
      if (height) setBlueBannerHeight(height);
    }
  }, [blueBannerRef, setBlueBannerHeight]);

  return (
    <div
      className={`${topSectionclassName}  ${styles.backgroundColorMobile}  flex flex-col flex-1 text-white`}
    >
      {renderExclusiveBanner(
        isShowExclusiveBanner.isShow && !isPromoBannerActive,
        isShowExclusiveBanner.bgColor,
        "Online price",
        application,
        cover?.showOfflinePrice,
        cover?.loyaltyDiscountEligible
      )}
      <div
        className={`${
          brand === BRAND_AVANTI && !isPromoBannerActive
            ? "pl-spacing-m"
            : "pl-spacing-s"
        } pb-spacing-s pt-spacing-xs pr-spacing-xs flex-1 flex flex-col`}
      >
        <div
          className={` ${
            brand === BRAND_AVANTI
              ? "text-border-accent-1-primary text-[18px] leading-[21px]"
              : "text-[14px] text-dark-text-on-primary leading-normal"
          } font-header-font  font-[400]`}
        >
          <p>{cardTitle}</p>
        </div>

        {type === ANNUAL_MULTI_TRIP &&
          referenceId &&
          !(
            cover?.collectionAmount === null ||
            cover?.collectionAmount === undefined
          ) && (
            <div>
              <p
                className={`${offlinePrice === "" ? "pt-[12px]" : "py-[6px]"} ${
                  brand === BRAND_AVANTI
                    ? "text-surface-neutral-option-9"
                    : "text-dark-text-on-primary"
                }`}
              >
                {offlinePrice}
              </p>
              {/* <br /> */}
              <p
                className={`font-body-font text-s font-bold leading-normal ${
                  brand === BRAND_AVANTI
                    ? "text-surface-neutral-option-9"
                    : "text-dark-text-on-primary"
                } `}
              >
                {renderTotalPrice(
                  cover?.total,
                  cover?.onlinePresentationTotalWithDiscount
                )}
              </p>
            </div>
          )}
        {type === ANNUAL_MULTI_TRIP &&
        referenceId &&
        !(
          cover?.collectionAmount === null ||
          cover?.collectionAmount === undefined
        ) ? (
          <div
            className={`${
              brand === BRAND_AVANTI && offlinePrice === ""
                ? "mt-spacing-xxs mb-spacing-s"
                : " mb-spacing-xs"
            } `}
          >
            <div
              className={`font-body-font text-[14px] font-normal  ${
                brand === BRAND_AVANTI
                  ? "text-surface-neutral-option-9 leading-[14px]"
                  : "text-dark-text-on-primary leading-4"
              }`}
            >
              <p>
                <span>or</span>
                <br />
                {cover
                  ? `${
                      (current.brandSpecificParams?.currencySymbol as string) ??
                      ""
                    }${cover.collectionAmount?.toFixed(2)}`
                  : ""}

                <span>&nbsp;/mon</span>
              </p>
            </div>
            {/* <p
              className={` leading-4 font-body-font font-normal ${brand === BRAND_AVANTI ? "text-surface-neutral-option-9 text-[18px]" : "text-dark-text-on-primary text-[14px]"}`}
            >
              ({`${currencySymbol}${cover.totalCost?.toFixed(2)}`} total)
            </p> */}
          </div>
        ) : (
          referenceId && (
            <div>
              <p className="py-spacing-xxs leading-[10px]">
                {(isPromoBannerActive ? hasPromoDiscount : true) &&
                  renderTotalPriceWithoutDiscounts(
                    hasPromoDiscount ? cover?.total : cover?.offlineTotal,
                    application,
                    cover?.loyaltyDiscountEligible,
                    cover?.finalRetailENBPrice,
                    cover?.showOfflinePrice
                  )}
              </p>
              {/* <br /> */}
              <p
                className={`font-body-font text-s font-bold leading-normal ${
                  isPromoCodeApplied ? "mb-[6px]" : "mb-spacing-xs"
                } ${
                  brand === BRAND_AVANTI
                    ? "text-surface-neutral-option-9"
                    : "text-dark-text-on-primary"
                }`}
              >
                {cover &&
                  renderTotalPrice(
                    cover?.total,
                    cover?.onlinePresentationTotalWithDiscount
                  )}
              </p>
            </div>
          )
        )}

        {isPromoCodeApplied && (
          <div className="mb-spacing-xs w-fit h-fit">
            <AppliedPromo promoCode={appliedPromoCode.code} />
          </div>
        )}

        <div
          className="flex items-center h-auto min-mt-spacing-xs mt-auto"
          data-testid="select-btn-wrapper-testid"
        >
          <SmallButtonWrapper
            id={`cover-level-selection-${id}-card-select-button`}
            data-testid={`cover-level-selection-${id}-card-select-button`}
            activeLabel={cardTopData?.selectBtnActiveLable ?? ""}
            inactiveLabel={cardTopData?.selectBtnInactiveLable ?? ""}
            hierarchy="primary"
            isRightIcon={false}
            className={`${styles.selectBtn} ${
              !slectState ? "blockClass" : ""
            } ${
              brand === BRAND_AVANTI
                ? "!text-surface-neutral-option-9"
                : "!text-border-strong"
            } max-h-[40px] min-w-[74px] !text-[18px] leading-[100%] font-[600] font-body-font p-spacing-s disabled:cursor-not-allowed`}
            onClick={(state) =>
              onSelect &&
              onSelect(keyValue as keyof typeof headerClassNames, state, id)
            }
            state={slectState}
            isLoading={cardTopData.isLoading}
            loadingIcon={<BtnLoadingSvg />}
            isClickableDisabled={
              referenceId === "" || referenceId === undefined
            }
            isDisabled={isQuoteDisabled}
            buttonWidthAuto
          />
        </div>
      </div>
    </div>
  );
}

export default withBrand(withApplication(Cover));
